<template>
  <div class="project-gantt">
    <div v-if="loading" class="gantt-loading">Loading gantt…</div>
    <div v-else-if="error" class="gantt-error">{{ error }}</div>
    <div v-else class="gantt-content">
      <div v-if="data.dateRange.min" class="gantt-range">
        {{ data.dateRange.min }} — {{ data.dateRange.max }}
      </div>
      <div class="gantt-chart">
        <div
          v-for="row in data.rows"
          :key="row.projectId"
          class="gantt-row"
        >
          <div class="gantt-label">
            <strong>{{ row.name }}</strong>
            <span class="gantt-customer">{{ row.customer }}</span>
          </div>
          <div class="gantt-bars">
            <div
              v-for="bar in row.bars"
              :key="bar.milestoneId"
              class="gantt-bar"
              :class="barClass(bar)"
              :title="barTooltip(bar)"
            >
              <span class="gantt-bar-label">{{ bar.title }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue'

const props = defineProps<{ url: string }>()

interface Bar {
  milestoneId: number
  title: string
  position: number
  status: string
  startsOn: string | null
  dueOn: string | null
  overdue: boolean
  signoffStatus: string | null
  paymentReady: boolean
}

interface Row {
  projectId: number
  name: string
  status: string
  customer: string | null
  startsOn: string | null
  endsOn: string | null
  bars: Bar[]
}

interface GanttData {
  dateRange: { min?: string; max?: string }
  rows: Row[]
}

const data = ref<GanttData>({ dateRange: {}, rows: [] })
const loading = ref(true)
const error = ref<string | null>(null)

function barClass(bar: Bar): string {
  const classes = [`status-${bar.status}`]
  if (bar.overdue) classes.push('overdue')
  if (bar.paymentReady) classes.push('payment-ready')
  return classes.join(' ')
}

function barTooltip(bar: Bar): string {
  const parts = [bar.title, bar.status]
  if (bar.startsOn) parts.push(`Start: ${bar.startsOn}`)
  if (bar.dueOn) parts.push(`Due: ${bar.dueOn}`)
  if (bar.overdue) parts.push('OVERDUE')
  if (bar.signoffStatus) parts.push(`Signoff: ${bar.signoffStatus}`)
  if (bar.paymentReady) parts.push('Payment ready')
  return parts.join(' | ')
}

onMounted(async () => {
  try {
    const response = await fetch(props.url, {
      headers: { 'Accept': 'application/json' },
      credentials: 'same-origin'
    })
    if (!response.ok) throw new Error(`HTTP ${response.status}`)
    data.value = await response.json()
  } catch (e) {
    error.value = e instanceof Error ? e.message : 'Failed to load gantt'
  } finally {
    loading.value = false
  }
})
</script>
